import { Icon, ItemStatus, Loading, TimeProvider } from "@focus/focusUI";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContentHeader } from "../components/ContentHeader";
import type { ConjunctionRiskConfig } from "../components/conjunctions/conjunctionUiUtils";
import { EventEvolutionCard } from "../components/events/EventEvolutionCard";
import { EventRecordsPanel } from "../components/events/EventRecordsPanel";
import {
  formatDate,
  getEventDetectionTimes,
  getRecordId,
  normalizeEventEvolutionEvent,
} from "../components/events/eventEvolutionUtils";
import { EvolutionPlot } from "../components/grafos/EvolutionPlot";
import { withTimeSystem } from "../constants/orbitMetadata";
import { useEventsEvolution } from "../hooks/useEventsEvolution";
import { useSystemConfig } from "../hooks/useSystemConfig";
import { createFormattersObject, type FormatterMap } from "../helpers/utils";
import { useAvailableconjunctionHeaders } from "../services/configuration";

export const EventEvolutionContainer = () => {
  const { event, status, error, loadEvent } = useEventsEvolution();
  const { currentConfig } = useSystemConfig();
  const availableConjHeaders = useAvailableconjunctionHeaders();
  const navigate = useNavigate();
  const { uuid } = useParams();

  useEffect(() => {
    if (uuid) {
      loadEvent(uuid);
    }
  }, [uuid, loadEvent]);

  const normalizedEvent = useMemo(
    () => normalizeEventEvolutionEvent(event),
    [event],
  );
  const records = useMemo(
    () => normalizedEvent?.conjunctions ?? [],
    [normalizedEvent],
  );
  const detectionTimes = useMemo(
    () => getEventDetectionTimes(records),
    [records],
  );
  const formatters = useMemo<FormatterMap>(
    () => createFormattersObject(availableConjHeaders.value),
    [availableConjHeaders.value],
  );
  const firstDetection = detectionTimes[0] ?? "-";
  const latestDetection = detectionTimes.at(-1) ?? "-";
  const activeRecordId = records.length > 0 ? getRecordId(records[0], 0) : "";

  if (status === ItemStatus.PENDING) {
    return (
      <div className="foc-content flex items-center justify-center p-8">
        <Loading />
      </div>
    );
  }

  if (error || !normalizedEvent) {
    return (
      <div className="foc-content flex items-center justify-center p-8 text-slate-500">
        {error || "Event not found."}
      </div>
    );
  }

  return (
    <div className="foc-content flex flex-col overflow-hidden">
      <ContentHeader
        renderCustomTitle={() => (
          <div className="flex min-w-0 flex-col gap-2">
            <button
              className="-ml-1 inline-flex w-fit cursor-pointer appearance-none items-center gap-1 rounded-md border-0 bg-transparent px-1.5 py-1 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              onClick={() => navigate(-1)}
              type="button"
            >
              <Icon icon="chevron-left" size={14} />
              Back
            </button>
          </div>
        )}
      />

      <main className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden">
        <section className="grid shrink-0 grid-cols-1 items-stretch gap-5 xl:grid-cols-[40%_minmax(0,1fr)]">
          <EventEvolutionCard
            event={normalizedEvent}
            formatters={formatters}
            riskConfig={currentConfig as ConjunctionRiskConfig}
          />
          <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
              <h2 className="text-base font-semibold text-slate-950">
                Event evolution
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-slate-500">
                  <span>
                    {withTimeSystem("First detection")}{" "}
                    {formatDate(firstDetection)}
                  </span>
                  <span className="text-slate-300">/</span>
                  <span>
                    {withTimeSystem("Latest detection")}{" "}
                    {formatDate(latestDetection)}
                  </span>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase text-slate-600">
                  <Icon icon="time" size={14} />
                  Timeline
                </span>
              </div>
            </div>
            <div className="min-h-0 flex-1 p-3">
              <TimeProvider>
                <EvolutionPlot
                  criticalRecord={normalizedEvent}
                  id={Number(uuid) || 1}
                  records={records}
                  tca={normalizedEvent.tca}
                />
              </TimeProvider>
            </div>
          </div>
        </section>

        <section className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <EventRecordsPanel
            activeRecordId={activeRecordId}
            formatters={formatters}
            records={records}
            riskConfig={currentConfig as ConjunctionRiskConfig}
          />
        </section>
      </main>
    </div>
  );
};
